// Reminders tab component - Show schedule reminders for all accounts
import { authHeaders } from "../config";

import { useState, useCallback, useEffect } from "react";
import styles from "./RemindersTab.module.css";
import { apiUrl } from "../config";

interface AccountReminder {
  id: number | string;
  name: string;
  type: "metricool" | "payhip";
  lastScheduledDate: string | null;
  daysRemaining: number | null;
  status: "ok" | "warning" | "urgent" | "empty";
  scheduledCount: number;
}

interface RemindersSummary {
  urgent: number;
  warning: number;
  empty: number;
  ok: number;
}

interface RemindersResponse {
  reminders: AccountReminder[];
  warningDays: number;
  summary: RemindersSummary;
}

export function RemindersTab() {
  const [reminders, setReminders] = useState<AccountReminder[]>([]);
  const [summary, setSummary] = useState<RemindersSummary | null>(null);
  const [warningDays, setWarningDays] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "urgent" | "warning" | "empty" | "ok">("all");

  const loadReminders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        apiUrl(`/api/reminders?warning_days=${warningDays}`),
        { headers: authHeaders() }
      );

      if (response.ok) {
        const data: RemindersResponse = await response.json();
        setReminders(data.reminders);
        setSummary(data.summary);
      } else {
        setError("Failed to load reminders");
      }
    } catch (err) {
      console.error("Failed to load reminders:", err);
      setError("Failed to load reminders");
    } finally {
      setIsLoading(false);
    }
  }, [warningDays]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const filteredReminders = filter === "all"
    ? reminders
    : reminders.filter(r => r.status === filter);

  const getStatusIcon = (status: AccountReminder["status"]) => {
    switch (status) {
      case "urgent":
        return "🔴";
      case "warning":
        return "🟡";
      case "empty":
        return "⚪";
      case "ok":
        return "🟢";
    }
  };

  const getStatusText = (reminder: AccountReminder) => {
    if (reminder.status === "empty") {
      return "No scheduled posts";
    }
    if (reminder.daysRemaining === null) {
      return "Unknown";
    }
    if (reminder.daysRemaining <= 0) {
      return "Posts end today or already ended!";
    }
    if (reminder.daysRemaining === 1) {
      return "Posts end tomorrow!";
    }
    return `${reminder.daysRemaining} days of posts remaining`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Schedule Reminders</h2>
        <div className={styles.headerActions}>
          <label className={styles.warningLabel}>
            Warn before:
            <input
              type="number"
              min="1"
              max="30"
              value={warningDays}
              onChange={(e) => setWarningDays(Math.max(1, parseInt(e.target.value) || 5))}
              className={styles.warningInput}
            />
            days
          </label>
          <button onClick={loadReminders} className={styles.refreshBtn} disabled={isLoading}>
            {isLoading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={() => setError(null)} className={styles.dismissBtn}>×</button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className={styles.summaryCards}>
          <button
            className={`${styles.summaryCard} ${styles.urgent} ${filter === "urgent" ? styles.active : ""}`}
            onClick={() => setFilter(filter === "urgent" ? "all" : "urgent")}
          >
            <span className={styles.summaryIcon}>🔴</span>
            <span className={styles.summaryCount}>{summary.urgent}</span>
            <span className={styles.summaryLabel}>Urgent</span>
          </button>
          <button
            className={`${styles.summaryCard} ${styles.warning} ${filter === "warning" ? styles.active : ""}`}
            onClick={() => setFilter(filter === "warning" ? "all" : "warning")}
          >
            <span className={styles.summaryIcon}>🟡</span>
            <span className={styles.summaryCount}>{summary.warning}</span>
            <span className={styles.summaryLabel}>Warning</span>
          </button>
          <button
            className={`${styles.summaryCard} ${styles.empty} ${filter === "empty" ? styles.active : ""}`}
            onClick={() => setFilter(filter === "empty" ? "all" : "empty")}
          >
            <span className={styles.summaryIcon}>⚪</span>
            <span className={styles.summaryCount}>{summary.empty}</span>
            <span className={styles.summaryLabel}>Empty</span>
          </button>
          <button
            className={`${styles.summaryCard} ${styles.ok} ${filter === "ok" ? styles.active : ""}`}
            onClick={() => setFilter(filter === "ok" ? "all" : "ok")}
          >
            <span className={styles.summaryIcon}>🟢</span>
            <span className={styles.summaryCount}>{summary.ok}</span>
            <span className={styles.summaryLabel}>OK</span>
          </button>
        </div>
      )}

      {/* Reminders List */}
      {isLoading && reminders.length === 0 ? (
        <div className={styles.loading}>Loading reminders...</div>
      ) : filteredReminders.length === 0 ? (
        <div className={styles.empty}>
          {filter === "all"
            ? "No accounts found. Add Metricool or Payhip accounts in Settings."
            : `No accounts with "${filter}" status.`}
        </div>
      ) : (
        <div className={styles.remindersList}>
          {filteredReminders.map((reminder) => (
            <div
              key={`${reminder.type}-${reminder.id}`}
              className={`${styles.reminderCard} ${styles[reminder.status]}`}
            >
              <div className={styles.reminderHeader}>
                <span className={styles.statusIcon}>{getStatusIcon(reminder.status)}</span>
                <span className={styles.accountName}>{reminder.name}</span>
                <span className={styles.accountType}>
                  {reminder.type === "metricool" ? "📊 Metricool" : "🛒 Payhip"}
                </span>
              </div>
              <div className={styles.reminderBody}>
                <div className={styles.statusMessage}>{getStatusText(reminder)}</div>
                <div className={styles.reminderDetails}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Last scheduled:</span>
                    <span className={styles.detailValue}>
                      {formatDate(reminder.lastScheduledDate)}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Scheduled posts:</span>
                    <span className={styles.detailValue}>{reminder.scheduledCount}</span>
                  </div>
                </div>
              </div>
              {(reminder.status === "urgent" || reminder.status === "warning") && (
                <div className={styles.reminderAction}>
                  <span className={styles.actionHint}>
                    Go to {reminder.type === "metricool" ? "Pin Jobs" : "Scraper"} tab to schedule more posts
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
